import { fetcher } from '#lib/backendFetcher';

/**
 * Parses search params JSON from a chunk marker and invokes the callback.
 */
const tryParseSearchParams = (paramsMatch, onSearchParams) => {
	try {
		if (paramsMatch?.[1]) onSearchParams?.(JSON.parse(paramsMatch[1]));
	} catch {
		/* ignore parse error */
	}
};

/**
 * Sends a message in the AI search conversation and streams the response via SSE.
 * @param {Object} params
 * @param {Array<{role: string, content: string}>} params.messages - Previous conversation history
 * @param {string} params.content - New user message
 * @param {Function} params.onChunk - Callback for each text chunk
 * @param {Function} params.onSearchParams - Callback when search parameters are extracted
 * @param {Function} params.onComplete - Callback when the stream ends
 * @param {Function} params.onError - Callback on errors
 * @param {Function} params.onSearchParamsError - Callback on search parameter validation errors
 * @returns {Promise<void>}
 */
export const sendAgenticMessage = async ({
	messages,
	content,
	onChunk,
	onSearchParams,
	onComplete,
	onError,
	onSearchParamsError,
}) => {
	try {
		const response = await fetcher('/api/v1/search/agentic/message', {
			method: 'POST',
			body: { messages, content },
			responseType: 'stream',
		});

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let fullResponse = '';

		// eslint-disable-next-line no-constant-condition
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;

			const chunk = decoder.decode(value, { stream: true });
			fullResponse += chunk;

			if (chunk.includes('[search_params]')) {
				const parts = chunk.split('\n[search_params]');
				if (parts[0]) onChunk?.(parts[0]);

				const remaining = '[search_params]' + (parts[1] || '');
				const paramsMatch = remaining.match(/\[search_params\](.*?)(?:\n|$)/);
				tryParseSearchParams(paramsMatch, onSearchParams);
			} else if (chunk.includes('[stream_error]')) {
				onError?.(new Error('Stream error'));
			} else if (chunk.includes('[search_params_error]')) {
				const errorMatch = chunk.match(/\[search_params_error\](.*)/);
				const errorMessage = errorMatch?.[1] || 'Validierungsfehler';
				const textPart = chunk.split('\n[search_params_error]')[0];
				if (textPart) onChunk?.(textPart);
				onSearchParamsError?.(errorMessage);
			} else {
				onChunk?.(chunk);
			}
		}

		onComplete?.(fullResponse);
	} catch (error) {
		onError?.(error);
	}
};

/**
 * Fetches a synchronous HR preview with 3 sample companies.
 * @param {Object} searchParams - Validated search parameters
 * @returns {Promise<{companies: Array<Object>}>}
 */
export const fetchPreviewResults = async (searchParams) => {
	return await fetcher('/api/v1/search/agentic/preview', {
		method: 'POST',
		body: { searchParams },
	});
};

/**
 * Executes the full AI search after approval.
 * @param {Object} params
 * @param {Object} params.searchParams - Validated search parameters
 * @param {string} [params.queryName] - Optional name for the prospect query
 * @returns {Promise<{status: string, jobId: string, prospectQueryId: string}>}
 */
export const executeAgenticSearch = async ({ searchParams, queryName }) => {
	return await fetcher('/api/v1/search/agentic/execute', {
		method: 'POST',
		body: { searchParams, queryName },
	});
};
